import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { PlatformClient } from "../../platform/supabase";
import "./WalletSharePrivacyStudio.css";

type CatalogItem={
  id:string;
  title:string;
  subtitle?:string|null;
  occurredAt?:string|null;
};

type ShareCatalog={
  experiences:CatalogItem[];
  competencies:CatalogItem[];
  achievements:CatalogItem[];
  credentials:CatalogItem[];
};

type ShareLink={
  shareId:string;
  label:string;
  status:string;
  includeExperiences:boolean;
  includeGrowth:boolean;
  includeCredentials:boolean;
  shareUrl:string;
  expiresAt:string;
  createdAt:string;
  audienceType?:string|null;
  purpose?:string|null;
  displayNameMode?:string|null;
  includeSchoolName?:boolean;
  scopeMode?:string|null;
  selectedExperienceCount?:number;
  selectedCompetencyCount?:number;
  selectedAchievementCount?:number;
  selectedCredentialCount?:number;
};

type SharePreview={
  label:string;
  expiresAt?:string|null;
  audienceType:string;
  purpose?:string|null;
  privacy:{
    displayNameMode:string;
    includeSchoolName:boolean;
    scopeMode:string;
  };
  profile:{
    displayName:string;
    schoolName?:string|null;
  };
  experiences:Array<{
    participationId:string;
    programTitle:string;
    activityTitle:string;
    activityType:string;
    completedAt?:string|null;
    attendedAt?:string|null;
  }>;
  growth:{
    competencies:Array<{
      competencyResultId:string;
      name:string;
      score:number;
      observedAt:string;
    }>;
    achievements:Array<{
      achievementId:string;
      name:string;
      description?:string|null;
      periodKey:string;
      confirmedAt?:string|null;
    }>;
  };
  credentials:Array<{
    credentialId:string;
    credentialName:string;
    achievementName:string;
    issuerName:string;
    status:string;
    isValid:boolean;
    issuedAt:string;
  }>;
};

type CreatedShare={
  shareId:string;
  shareUrl:string;
  expiresAt:string;
};

const audienceOptions=[
  ["GENERAL","일반 공유"],
  ["SCHOOL","학교·교사"],
  ["MENTOR","멘토·코치"],
  ["COLLEGE","대학·진학"],
  ["EMPLOYER","기업·기관"],
  ["PROGRAM","프로그램 담당자"],
  ["OTHER","기타"]
] as const;

const nameModeOptions=[
  ["FULL","이름 전체"],
  ["MASKED","이름 일부 가림"],
  ["HIDDEN","이름 비공개"]
] as const;

function formatDate(value?:string|null){
  if(!value)return "-";
  const date=new Date(value);
  if(Number.isNaN(date.getTime()))return value;
  return date.toLocaleDateString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit"});
}

function audienceLabel(value?:string|null){
  return audienceOptions.find(([key])=>key===value)?.[1] || value || "일반 공유";
}

function statusLabel(value:string){
  const labels:Record<string,string>={
    ACTIVE:"활성",
    REVOKED:"종료",
    EXPIRED:"만료"
  };
  return labels[value] || value;
}

async function copyText(value:string){
  await navigator.clipboard.writeText(value);
}

export function WalletSharePrivacyStudio({client}:{client:PlatformClient}){
  const [catalog,setCatalog]=useState<ShareCatalog|null>(null);
  const [items,setItems]=useState<ShareLink[]>([]);
  const [preview,setPreview]=useState<SharePreview|null>(null);
  const [latest,setLatest]=useState<CreatedShare|null>(null);

  const [label,setLabel]=useState("");
  const [audience,setAudience]=useState("GENERAL");
  const [purpose,setPurpose]=useState("");
  const [nameMode,setNameMode]=useState("MASKED");
  const [includeSchool,setIncludeSchool]=useState(false);
  const [includeExperiences,setIncludeExperiences]=useState(true);
  const [includeGrowth,setIncludeGrowth]=useState(true);
  const [includeCredentials,setIncludeCredentials]=useState(true);
  const [scopeMode,setScopeMode]=useState<"ALL"|"SELECTED">("ALL");
  const [validDays,setValidDays]=useState(30);

  const [experienceIds,setExperienceIds]=useState<Set<string>>(new Set());
  const [competencyIds,setCompetencyIds]=useState<Set<string>>(new Set());
  const [achievementIds,setAchievementIds]=useState<Set<string>>(new Set());
  const [credentialIds,setCredentialIds]=useState<Set<string>>(new Set());

  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");

  async function load(){
    const [catalogResult,linksResult]=await Promise.all([
      client.rpc("wallet_share_catalog"),
      client.rpc("wallet_share_links")
    ]);
    if(catalogResult.error){setError(catalogResult.error.message);return;}
    if(linksResult.error){setError(linksResult.error.message);return;}
    setCatalog(catalogResult.data as ShareCatalog);
    setItems((linksResult.data || []) as ShareLink[]);
    setError("");
  }

  useEffect(()=>{void load();},[client]);

  const selectedTotal=useMemo(()=>
    (includeExperiences ? experienceIds.size : 0)
    +(includeGrowth ? competencyIds.size+achievementIds.size : 0)
    +(includeCredentials ? credentialIds.size : 0),
    [includeExperiences,includeGrowth,includeCredentials,experienceIds,competencyIds,achievementIds,credentialIds]
  );

  function toggle(setter:React.Dispatch<React.SetStateAction<Set<string>>>,id:string){
    setter((current)=>{
      const next=new Set(current);
      if(next.has(id))next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function args(){
    return {
      p_include_experiences:includeExperiences,
      p_include_growth:includeGrowth,
      p_include_credentials:includeCredentials,
      p_scope_mode:scopeMode,
      p_experience_ids:Array.from(experienceIds),
      p_competency_result_ids:Array.from(competencyIds),
      p_achievement_ids:Array.from(achievementIds),
      p_credential_ids:Array.from(credentialIds),
      p_display_name_mode:nameMode,
      p_include_school_name:includeSchool,
      p_audience_type:audience,
      p_purpose:purpose.trim() || null
    };
  }

  async function makePreview(){
    setBusy(true);setError("");setNotice("");
    const {data,error}=await client.rpc("wallet_share_preview",args());
    if(error){setError(error.message);setBusy(false);return;}
    setPreview(data as SharePreview);
    setLatest(null);
    setBusy(false);
  }

  async function createShare(){
    if(!label.trim())return;
    setBusy(true);setError("");setNotice("");
    const {data,error}=await client.rpc("wallet_create_share_v2",{
      p_label:label.trim(),
      p_audience_type:audience,
      p_purpose:purpose.trim() || null,
      p_display_name_mode:nameMode,
      p_include_school_name:includeSchool,
      p_include_experiences:includeExperiences,
      p_include_growth:includeGrowth,
      p_include_credentials:includeCredentials,
      p_scope_mode:scopeMode,
      p_experience_ids:Array.from(experienceIds),
      p_competency_result_ids:Array.from(competencyIds),
      p_achievement_ids:Array.from(achievementIds),
      p_credential_ids:Array.from(credentialIds),
      p_valid_days:validDays
    });
    if(error){setError(error.message);setBusy(false);return;}
    const created=data as CreatedShare;
    setLatest(created);
    setNotice("새 공유 링크를 만들었습니다.");
    await load();
    setBusy(false);
  }

  async function revoke(shareId:string){
    setBusy(true);setError("");setNotice("");
    const {error}=await client.rpc("wallet_revoke_share",{p_share_id:shareId});
    if(error){setError(error.message);setBusy(false);return;}
    setNotice("공유를 종료했습니다.");
    if(latest?.shareId===shareId)setLatest(null);
    await load();
    setBusy(false);
  }

  if(!catalog && !error)return <p className="muted">공유할 기록을 불러오는 중…</p>;

  return (
    <section className="wallet-page share-privacy-page">
      <div className="share-privacy-hero">
        <div>
          <p className="eyebrow">PASSPORT SHARE & PRIVACY</p>
          <h2>보여줄 기록만 골라서 공유해요</h2>
          <p>누구에게, 어떤 목적으로, 어떤 기록을 보여줄지 직접 정하고 공유 전 화면을 먼저 확인할 수 있습니다.</p>
        </div>
        <div className="share-privacy-principle">
          <strong>최소 공개 원칙</strong>
          <span>필요한 기록만 선택하고, 이름·학교명 공개 범위도 직접 조절합니다.</span>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="share-privacy-notice">{notice}</p>}

      <div className="share-studio-grid">
        <section className="share-policy-panel">
          <header>
            <div>
              <p className="eyebrow">01 · PURPOSE</p>
              <h3>공유 목적</h3>
            </div>
          </header>

          <label>
            공유 이름
            <input value={label} onChange={(e)=>setLabel(e.target.value)} maxLength={120} placeholder="예: 진로상담용 공유" />
          </label>

          <div className="share-form-two">
            <label>
              공유 대상
              <select value={audience} onChange={(e)=>setAudience(e.target.value)}>
                {audienceOptions.map(([value,text])=><option value={value} key={value}>{text}</option>)}
              </select>
            </label>
            <label>
              유효 기간
              <select value={validDays} onChange={(e)=>setValidDays(Number(e.target.value))}>
                <option value={1}>1일</option>
                <option value={7}>7일</option>
                <option value={14}>14일</option>
                <option value={30}>30일</option>
                <option value={90}>90일</option>
                <option value={180}>180일</option>
                <option value={365}>365일</option>
              </select>
            </label>
          </div>

          <label>
            목적 메모
            <textarea value={purpose} onChange={(e)=>setPurpose(e.target.value)} placeholder="예: 진로상담 전에 나의 활동과 Life Stamp를 보여주기 위해" />
          </label>

          <div className="share-privacy-options">
            <label>
              이름 표시
              <select value={nameMode} onChange={(e)=>setNameMode(e.target.value)}>
                {nameModeOptions.map(([value,text])=><option value={value} key={value}>{text}</option>)}
              </select>
            </label>
            <label className="share-check-row">
              <input type="checkbox" checked={includeSchool} onChange={(e)=>setIncludeSchool(e.target.checked)} />
              학교명 공개
            </label>
          </div>
        </section>

        <section className="share-policy-panel">
          <header>
            <div>
              <p className="eyebrow">02 · CONTENT</p>
              <h3>공유할 내용</h3>
            </div>
          </header>

          <div className="share-category-switches">
            <label><input type="checkbox" checked={includeExperiences} onChange={(e)=>setIncludeExperiences(e.target.checked)} /> 경험</label>
            <label><input type="checkbox" checked={includeGrowth} onChange={(e)=>setIncludeGrowth(e.target.checked)} /> 성장·성취</label>
            <label><input type="checkbox" checked={includeCredentials} onChange={(e)=>setIncludeCredentials(e.target.checked)} /> Life Stamp</label>
          </div>

          <div className="share-scope-switch">
            <button className={scopeMode==="ALL" ? "active" : ""} onClick={()=>setScopeMode("ALL")}>해당 영역 전체</button>
            <button className={scopeMode==="SELECTED" ? "active" : ""} onClick={()=>setScopeMode("SELECTED")}>기록 직접 선택</button>
          </div>

          {scopeMode==="SELECTED" && catalog && (
            <div className="share-record-picker">
              {includeExperiences && (
                <SharePickerGroup title="경험" items={catalog.experiences} selected={experienceIds} onToggle={(id)=>toggle(setExperienceIds,id)} />
              )}
              {includeGrowth && (
                <>
                  <SharePickerGroup title="역량 관찰" items={catalog.competencies} selected={competencyIds} onToggle={(id)=>toggle(setCompetencyIds,id)} />
                  <SharePickerGroup title="성취" items={catalog.achievements} selected={achievementIds} onToggle={(id)=>toggle(setAchievementIds,id)} />
                </>
              )}
              {includeCredentials && (
                <SharePickerGroup title="Life Stamp" items={catalog.credentials} selected={credentialIds} onToggle={(id)=>toggle(setCredentialIds,id)} />
              )}
              <div className="share-selected-count">선택한 기록 {selectedTotal}개</div>
            </div>
          )}
        </section>
      </div>

      <section className="share-preview-actions">
        <div>
          <strong>공유하기 전에 먼저 확인하세요</strong>
          <span>미리보기는 링크를 만들지 않습니다.</span>
        </div>
        <div>
          <button className="secondary" disabled={busy || !(includeExperiences||includeGrowth||includeCredentials) || (scopeMode==="SELECTED" && selectedTotal===0)} onClick={makePreview}>
            공유 화면 미리보기
          </button>
          <button className="primary" disabled={busy || !label.trim() || !(includeExperiences||includeGrowth||includeCredentials) || (scopeMode==="SELECTED" && selectedTotal===0)} onClick={createShare}>
            공유 링크 만들기
          </button>
        </div>
      </section>

      {preview && (
        <SharePreviewCard preview={preview} />
      )}

      {latest && (
        <section className="share-created-card">
          <div className="share-created-qr"><QRCodeSVG value={latest.shareUrl} size={152} level="M" /></div>
          <div>
            <p className="eyebrow">SHARE READY</p>
            <h3>공유 링크가 준비되었습니다</h3>
            <p>{latest.shareUrl}</p>
            <small>만료 {formatDate(latest.expiresAt)}</small>
            <div className="share-created-actions">
              <button className="secondary" onClick={()=>copyText(latest.shareUrl)}>링크 복사</button>
              <a href={latest.shareUrl} target="_blank" rel="noreferrer">공개 화면 보기</a>
            </div>
          </div>
        </section>
      )}

      <section className="share-history-panel">
        <header>
          <div>
            <p className="eyebrow">SHARE HISTORY</p>
            <h3>내 공유 관리</h3>
          </div>
          <span>{items.length}개</span>
        </header>

        <div className="share-history-list">
          {items.length ? items.map((item)=>(
            <article key={item.shareId} className="share-history-card">
              <div>
                <span>{audienceLabel(item.audienceType)} · {item.scopeMode==="SELECTED" ? "선택 공유" : "전체 공유"}</span>
                <strong>{item.label}</strong>
                <small>{item.purpose || "목적 메모 없음"} · 만료 {formatDate(item.expiresAt)}</small>
              </div>
              <div className="share-history-meta">
                <em>{statusLabel(item.status)}</em>
                <span>
                  {item.scopeMode==="SELECTED"
                    ? `선택 ${(item.selectedExperienceCount||0)+(item.selectedCompetencyCount||0)+(item.selectedAchievementCount||0)+(item.selectedCredentialCount||0)}개`
                    : "영역 전체"}
                </span>
              </div>
              <div className="share-history-actions">
                <button onClick={()=>copyText(item.shareUrl)}>복사</button>
                <a href={item.shareUrl} target="_blank" rel="noreferrer">보기</a>
                {item.status==="ACTIVE" && <button disabled={busy} onClick={()=>revoke(item.shareId)}>공유 종료</button>}
              </div>
            </article>
          )):<div className="share-empty">아직 만든 공유 링크가 없습니다.</div>}
        </div>
      </section>

      <div className="share-privacy-footnote">
        <strong>공유되지 않는 것</strong>
        <span>개인 증빙 파일의 원문, 학생 성찰 원문, 교사용 사례관리 정보는 이 공유 화면에 포함하지 않습니다.</span>
      </div>
    </section>
  );
}

function SharePickerGroup({
  title,items,selected,onToggle
}:{
  title:string;
  items:CatalogItem[];
  selected:Set<string>;
  onToggle:(id:string)=>void;
}){
  return (
    <section className="share-picker-group">
      <header><strong>{title}</strong><span>{items.length}개</span></header>
      {items.length ? items.map((item)=>(
        <label className="share-picker-item" key={item.id}>
          <input type="checkbox" checked={selected.has(item.id)} onChange={()=>onToggle(item.id)} />
          <div>
            <strong>{item.title}</strong>
            <span>{item.subtitle || "기록"}</span>
            <small>{formatDate(item.occurredAt)}</small>
          </div>
        </label>
      )):<div className="share-picker-empty">선택할 기록이 없습니다.</div>}
    </section>
  );
}

function SharePreviewCard({preview}:{preview:SharePreview}){
  const count=
    preview.experiences.length
    +preview.growth.competencies.length
    +preview.growth.achievements.length
    +preview.credentials.length;

  return (
    <section className="share-preview-card">
      <header>
        <div>
          <p className="eyebrow">PREVIEW</p>
          <h3>상대방에게 이렇게 보입니다</h3>
        </div>
        <span>{count}개 기록</span>
      </header>

      <div className="share-preview-profile">
        <strong>{preview.profile.displayName}</strong>
        <span>{preview.profile.schoolName || "학교명 비공개"}</span>
      </div>

      <div className="share-preview-summary">
        <div><span>경험</span><strong>{preview.experiences.length}</strong></div>
        <div><span>역량</span><strong>{preview.growth.competencies.length}</strong></div>
        <div><span>성취</span><strong>{preview.growth.achievements.length}</strong></div>
        <div><span>Life Stamp</span><strong>{preview.credentials.length}</strong></div>
      </div>

      <div className="share-preview-list">
        {preview.experiences.slice(0,3).map((item)=><span key={item.participationId}>경험 · {item.activityTitle}</span>)}
        {preview.growth.competencies.slice(0,3).map((item)=><span key={item.competencyResultId}>역량 · {item.name}</span>)}
        {preview.growth.achievements.slice(0,3).map((item)=><span key={item.achievementId}>성취 · {item.name}</span>)}
        {preview.credentials.slice(0,3).map((item)=><span key={item.credentialId}>Life Stamp · {item.credentialName}</span>)}
      </div>
    </section>
  );
}
